import {
  createContext, useContext, useEffect, ReactNode, useState, Dispatch,
} from 'react';
import { List } from 'lib/types';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { notify } from 'lib/utils/honeybadgerUtils';
import { isEmpty } from 'lodash';

type ProviderProps = { children: ReactNode; includeUsers?: boolean };
type ContextProps = { lists: List[] | undefined; setLists: Dispatch<List[]> };

const ListsContext = createContext<ContextProps>({} as ContextProps);

function useUserLists() {
  const context = useContext(ListsContext);
  if (isEmpty(context)) {
    throw new Error('useUserLists must be used within a UserListsProvider');
  }
  return context;
}

function UserListsProvider({ children, includeUsers = false }: ProviderProps) {
  const [lists, setLists] = useState<List[] | undefined>();

  const API = useApi();
  const currentUser = useCurrentUser();
  const value = { lists, setLists };

  useEffect(() => {
    const getLists = async () => {
      if (!currentUser) return;

      const maxRetries = 3;
      let retryCount = 0;
      let retryDelay = 2000; // 2-second delay

      /** Use exponential backoff to retry fetching user lists */
      const attemptGetUserLists = async (): Promise<List[]> => {
        try {
          const data = await API.getUserLists(includeUsers);
          return data.filter((l) => l.status === 'active');
        } catch (err) {
          console.error('User lists fetch failed:', err);

          if (retryCount < maxRetries) {
            retryCount += 1;
            console.log(`Retrying user lists fetch (${retryCount}/${maxRetries}) in ${retryDelay}ms...`);

            await new Promise((resolve) => setTimeout(resolve, retryDelay));

            retryDelay *= 2;

            return attemptGetUserLists();
          }

          console.error('User lists fetch failed after all retry attempts');
          notify(
            new Error('User lists fetch failed after all retry attempts'),
            'lib/contexts/userListsContext',
            {
              retryCount,
              retryDelay,
            },
          );
          return [];
        }
      };

      try {
        const data = await attemptGetUserLists();
        setLists(data);
      } catch (err) {
        console.error('Fatal error during user lists fetch:', err);
        setLists([]);
      }
    };

    if (!lists && currentUser?.id && API) getLists();
  }, [currentUser, API, lists]);

  return (
    <ListsContext.Provider value={value}>
      {children}
    </ListsContext.Provider>
  );
}

export { UserListsProvider, useUserLists };
